import { useCallback, useEffect, useState } from "react";
import { useIntl, type IntlShape } from "react-intl";

import {
  createInternalDialog,
  fetchInternalDialogs,
  updateInternalDialog,
} from "../api/tas";
import { useUserProfile } from "../configuration/login/loginSlice";
import type { ITasOccurrenceComment } from "../models/tasOccurrence";

const buildDialogTitle = (intl: IntlShape, username: string, date = new Date()) => {
  const formattedDate = date.toLocaleDateString(intl.locale, { timeZone: "America/Sao_Paulo" });
  const formattedTime = date.toLocaleTimeString(intl.locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });

  return intl.formatMessage(
    { id: "intl-msg:tas.support.details.chatVwco.commentTitle" },
    { username, date: formattedDate, time: formattedTime },
  );
};

const parseUtcIsoStringToDate = (value: string): Date | undefined => {
  let normalized = value;
  const hasTimezone = /Z$/.test(normalized) || /([+-]\d{2}:\d{2}|[+-]\d{2}\d{2})$/.test(normalized);
  if (!hasTimezone) normalized = `${normalized}Z`;
  normalized = normalized.replace(
    /(\.\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})$)/,
    "$1",
  );
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

type InternalDialogResponse = {
  id: number;
  tasOccurrenceId: number;
  title?: string;
  text?: string;
  comment?: string;
  author?: string;
  commentedBy?: string;
  availableToEdit?: boolean;
  wasUpdated?: boolean;
  createdAt?: string | Date;
  updatedAt?: string | Date;
};

const toDate = (value?: string | Date) => {
  if (value instanceof Date) return value;
  if (typeof value === "string") return parseUtcIsoStringToDate(value);
  return undefined;
};

const normalizeDialog = (raw: InternalDialogResponse): ITasOccurrenceComment => {
  const createdAt = toDate(raw.createdAt);
  const updatedAt = toDate(raw.updatedAt);

  return {
    id: raw.id,
    tasOccurrenceId: raw.tasOccurrenceId,
    comment: raw.comment ?? raw.text ?? "",
    commentedBy: raw.commentedBy ?? raw.author ?? "",
    availableToEdit: raw.availableToEdit ?? true,
    wasUpdated: raw.wasUpdated ?? Boolean(createdAt && updatedAt && createdAt.getTime() !== updatedAt.getTime()),
    createdAt,
    updatedAt,
  };
};

export const useTasOccurrenceChatVWCO = (occurrenceId: string, enabled = true) => {
  const intl = useIntl();
  const userProfile = useUserProfile();
  const username = userProfile?.name?.trim()
    || [userProfile?.givenName, userProfile?.familyName].filter(Boolean).join(" ").trim()
    || "";
  const [comments, setComments] = useState<ITasOccurrenceComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCommentText, setNewCommentText] = useState("");
  const [isNewComment, setIsNewComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [submittingNew, setSubmittingNew] = useState(false);
  const [savingEditUuid, setSavingEditUuid] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<ITasOccurrenceComment | null>(null);

  const loadComments = useCallback(async () => {
    if (!enabled || !occurrenceId) {
      setComments([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const data = await fetchInternalDialogs(occurrenceId);
      const list = (data ?? []).map(normalizeDialog);
      setComments(list.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)));
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [occurrenceId, enabled]);

  useEffect(() => {
    void loadComments();
  }, [loadComments]);

  useEffect(() => {
    setEditingCommentId(null);
    setEditDraft("");
  }, [occurrenceId]);

  const submitNewComment = async () => {
    const trimmed = newCommentText.trim();
    if (!occurrenceId || !trimmed || submittingNew) return;

    setSubmittingNew(true);
    setIsNewComment(true);

    try {
      const created = await createInternalDialog(occurrenceId, trimmed, buildDialogTitle(intl, username));
      if (created?.id) {
        setComments((prev) => [...prev, normalizeDialog(created)].sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)));
        setNewCommentText("");
      } else {
        await loadComments();
        setNewCommentText("");
      }
    } finally {
      setSubmittingNew(false);
    }
  };

  const beginEdit = (comment: ITasOccurrenceComment) => {
    const commentId = comment.id;
    if (!commentId) return;
    setEditingCommentId(commentId);
    setEditDraft(comment.comment ?? "");
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    if (!occurrenceId || !editingCommentId) return;
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    setSavingEditUuid(editingCommentId);
    const updated = await updateInternalDialog(occurrenceId, editingCommentId, trimmed, buildDialogTitle(intl, username));
    setSavingEditUuid(null);
    if (updated?.id) {
      setComments((prev) =>
        prev.map((c) => (c.id === editingCommentId ? normalizeDialog(updated) : c)).sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)),
      );
      cancelEdit();
    } else {
      await loadComments();
      cancelEdit();
    }
  };

  const removeComment = async (_commentId: number) => {
    setDeletingId(null);
  };

  return {
    comments,
    loading,
    newCommentText,
    submittingNew,
    editingCommentId,
    editDraft,
    savingEditUuid,
    deletingId,
    showConfirmDialog,
    commentToDelete,
    isNewComment,
    resetIsNewComment: () => setIsNewComment(false),

    setNewCommentText,
    setEditDraft,
    submitNewComment,
    beginEdit,
    cancelEdit,
    saveEdit,
    removeComment,
    setShowConfirmDialog,
    setCommentToDelete,
    reloadComments: loadComments,
  };
};
